import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CARTE, TITRE_CARTE } from "./reglages/cadre";

type Sending = "idle" | "sending" | "sent" | "closed" | "failed";

/**
 * L'ecran de connexion de la regie : une adresse, un lien magique, rien d'autre. Copie adaptee de
 * celui de la console du Lab (`apps/lab`, `states.tsx`) : le template ne livre pas d'ecran de
 * connexion (`src/lab/` ne pose que les pages legales et « Mes données »). Le jour ou il en livrera
 * un, ce composant se remplace par lui.
 *
 * `callbackURL` est la page ou le lien ramene, une fois la session ouverte (un chemin de l'app).
 * `notice` est une phrase deja ecrite par l'appelant (jamais un texte recu), posee au-dessus du
 * formulaire : par exemple « ce lien ne marche plus » quand un lien magique revient en echec.
 *
 * La liste blanche (`AUTH_ALLOWED_EMAILS`) est appliquee par le backend, pas ici : la reponse est la
 * meme, au meme moment, que l'adresse soit admise ou non. L'ecran ne promet donc pas qu'un lien est
 * parti, seulement qu'il l'est si l'adresse est admise.
 */
export function SignIn({
  callbackURL,
  notice,
}: {
  callbackURL: string;
  notice?: string | null | undefined;
}) {
  const [address, setAddress] = useState("");
  const [state, setState] = useState<Sending>("idle");

  // Le bouton est grise quand le champ est vide ou qu'une demande part : un navigateur n'envoie pas
  // non plus le formulaire par la touche Entree quand son bouton est grise.
  const request = async (): Promise<void> => {
    setState("sending");
    try {
      const response = await fetch("/api/auth/sign-in/magic-link", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: address.trim(), callbackURL }),
      });
      if (response.ok) {
        setState("sent");
      } else {
        setState(response.status === 503 ? "closed" : "failed");
      }
    } catch {
      setState("failed");
    }
  };

  return (
    <Card className={CARTE}>
      <CardHeader>
        <CardTitle className={TITRE_CARTE}>Connexion requise</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {notice ? (
          <p className="text-[20px] text-destructive" role="alert">
            {notice}
          </p>
        ) : null}
        <p className="text-[20px] text-muted-foreground">
          La régie envoie un lien de connexion par courrier électronique. Il est valable quinze
          minutes et n'ouvre la page que pour l'adresse inscrite sur la liste.
        </p>
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void request();
          }}
        >
          <input
            aria-label="Adresse électronique"
            autoComplete="email"
            className="h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-[20px]"
            onChange={(event) => setAddress(event.target.value)}
            placeholder="adresse@exemple.fr"
            type="email"
            value={address}
          />
          <Button disabled={state === "sending" || address.trim() === ""} type="submit">
            Recevoir le lien
          </Button>
        </form>
        {state === "sent" ? (
          <p className="text-[20px]" role="status">
            Si cette adresse est admise, un lien vient de partir. Regarde ta boîte, il vaut quinze
            minutes.
          </p>
        ) : null}
        {state === "closed" ? (
          <p className="text-[20px] text-destructive" role="alert">
            L'authentification n'est pas configurée sur ce serveur (AUTH_SECRET absent).
          </p>
        ) : null}
        {state === "failed" ? (
          <p className="text-[20px] text-destructive" role="alert">
            L'envoi n'a pas abouti. Réessaie dans quelques minutes.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
